import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast, ToastContainer } from "react-toastify";

import { AuthContext } from "../../../context/auth-context";
import Modal from "../../../Component/Modal/Modal";
import ConfirmModal from "../../../Component/ConfirmModal";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import Spinner from "../../../Component/Spinner/Spinner";
import EmptyView from "../../../Component/EmptyView/EmptyView";
import CategoryMonitoringForm from "./CategoryMonitoringForm";
import brandClasses from "../Brand/Brand.module.css";
import { getLlms, getMarkets } from "../../../services/apiService";
import {
  fetchCategoryMonitoringGroups,
  fetchVisibilityPromptTemplates,
  createCategoryMonitoringGroup,
  updateCategoryMonitoringGroup,
  deleteCategoryMonitoringGroup,
} from "../../../services/categoryMonitoringOnboardingService";

const TABLE_HEADERS = [
  { id: "category", name: "Category", sorting: false },
  { id: "market", name: "Market", sorting: false },
  { id: "brands", name: "Brands", sorting: false },
  { id: "llms", name: "LLMs", sorting: false },
  { id: "status", name: "Status", sorting: false },
  { id: "date_added", name: "Date Added", sorting: false },
  { id: "actions", name: "Actions", sorting: false },
];

const DEFAULT_FORM_VALUES = {
  customer_id: "",
  category: "",
  brands: "",
  market_id: "",
  visibility_prompt: "",
  llm_ids: [],
};

function formatDate(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-GB");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate} ${formattedTime}`;
}

// forwardRef + useImperativeHandle: Settings.js's shared top-right "Add X" header
// button lives outside this component, so it needs a way to trigger our create
// form without lifting all our modal state up. Same pattern as ConversationMonitoring.
const CategoryMonitoring = forwardRef(function CategoryMonitoring(_props, ref) {
  const authCtx = useContext(AuthContext);
  // Same access model as Conversation Monitoring: no customer picker for any role,
  // customer_id always comes from the same globally-synced AuthContext value every
  // other Settings tab already uses.
  const ownCustomerId = authCtx?.user?.customer_id || null;

  const [markets, setMarkets] = useState([]);
  const [llms, setLlms] = useState([]);
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    getMarkets()
      .then((res) => setMarkets(res?.data || []))
      .catch(() => toast.error("Failed to load markets"));
    getLlms()
      .then((res) => setLlms(res?.data || []))
      .catch(() => toast.error("Failed to load LLMs"));
  }, []);

  useEffect(() => {
    if (!ownCustomerId) return;
    fetchVisibilityPromptTemplates(ownCustomerId)
      .then(setPromptTemplates)
      .catch(() => setPromptTemplates([]));
  }, [ownCustomerId]);

  const loadGroups = useCallback(() => {
    if (!ownCustomerId) return;
    setGroupsLoading(true);
    fetchCategoryMonitoringGroups(ownCustomerId)
      .then(setGroups)
      .catch(() => toast.error("Failed to load category monitoring groups"))
      .finally(() => setGroupsLoading(false));
  }, [ownCustomerId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleOpenCreate = () => {
    setIsUpdateMode(false);
    setFormValues({ ...DEFAULT_FORM_VALUES, customer_id: ownCustomerId });
    setIsFormOpen(true);
  };

  useImperativeHandle(ref, () => ({ openCreate: handleOpenCreate }));

  const handleOpenEdit = (group) => {
    setIsUpdateMode(true);
    setFormValues({
      customer_id: ownCustomerId,
      category: group.category,
      brands: group.brands,
      market_id: group.market_id,
      visibility_prompt: group.visibility_prompt,
      llm_ids: group.llms.map((l) => l.id),
    });
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setFormValues(DEFAULT_FORM_VALUES);
  };

  const handleSubmitForm = async (values) => {
    try {
      if (isUpdateMode) {
        await updateCategoryMonitoringGroup({
          customer_id: values.customer_id,
          category: values.category,
          market_id: values.market_id,
          brands: values.brands,
          visibility_prompt: values.visibility_prompt,
          llm_ids: values.llm_ids,
        });
        toast.success("Category monitoring group updated");
      } else {
        if (!values.customer_id) {
          toast.error("Unable to determine your customer — please refresh and try again.");
          return;
        }
        await createCategoryMonitoringGroup({
          customer_id: values.customer_id,
          category: values.category,
          brands: values.brands,
          market_id: values.market_id,
          visibility_prompt: values.visibility_prompt,
          llm_ids: values.llm_ids,
        });
        toast.success("Category monitoring group created");
      }
      handleCloseForm();
      loadGroups();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleDeleteClick = (group) => {
    setDeleteTarget(group);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCategoryMonitoringGroup({
        customer_id: ownCustomerId,
        category: deleteTarget.category,
        market_id: deleteTarget.market_id,
      });
      toast.success("Category monitoring group deleted");
      loadGroups();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete group");
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const renderRow = (group) => (
    <StyledTableRow key={`${group.category}-${group.market_id}`}>
      <StyledTableCell style={{ width: 200 }}>{group.category}</StyledTableCell>
      <StyledTableCell style={{ width: 140 }}>{group.market_name || group.market_id}</StyledTableCell>
      <StyledTableCell style={{ width: 220 }}>{group.brands}</StyledTableCell>
      <StyledTableCell style={{ width: 220 }}>
        {group.llms.map((l) => l.display_name || l.name).join(", ")}
      </StyledTableCell>
      <StyledTableCell style={{ width: 120 }}>
        <div className={brandClasses.status}>
          <CheckCircleIcon style={{ color: group.is_active ? "#028101" : "#bdbdbd" }} />
          <span>{group.is_active ? "Active" : "Inactive"}</span>
        </div>
      </StyledTableCell>
      <StyledTableCell style={{ width: 180 }}>{formatDate(group.date_added)}</StyledTableCell>
      <StyledTableCell align="left">
        <div>
          <MUIIconButton
            tooltip="Update"
            style={{ color: "#007aff" }}
            className={brandClasses.action_btn}
            onClick={() => handleOpenEdit(group)}
          >
            <EditIcon />
          </MUIIconButton>
          <MUIIconButton
            tooltip="Delete"
            style={{ color: "#da1a04" }}
            className={brandClasses.action_btn}
            onClick={() => handleDeleteClick(group)}
          >
            <DeleteOutlineIcon />
          </MUIIconButton>
        </div>
      </StyledTableCell>
    </StyledTableRow>
  );

  return (
    <>
      {groupsLoading ? (
        <Spinner />
      ) : groups.length !== 0 ? (
        <Table headers={TABLE_HEADERS} data={groups} render={renderRow} />
      ) : (
        <EmptyView message="No category monitoring groups for this customer yet." />
      )}

      <Modal
        title={isUpdateMode ? "Update Category Monitoring" : "Add Category Monitoring"}
        open={isFormOpen}
        handleClose={handleCloseForm}
      >
        <CategoryMonitoringForm
          formValues={formValues}
          setFormValues={setFormValues}
          isUpdateMode={isUpdateMode}
          markets={markets}
          llms={llms}
          promptTemplates={promptTemplates}
          handleCloseModal={handleCloseForm}
          onSubmit={handleSubmitForm}
        />
      </Modal>

      <ConfirmModal
        show={showDeleteConfirm}
        handleClose={handleCancelDelete}
        handleConfirmDeletion={handleConfirmDelete}
        selectedId={deleteTarget?.category}
        title="Delete Category Monitoring Group?"
        message={
          deleteTarget
            ? `Are you sure you want to delete the group for "${deleteTarget.category}" (${deleteTarget.market_name || deleteTarget.market_id})? This removes all ${deleteTarget.llms.length} LLM configuration(s) in it.`
            : undefined
        }
      />

      <ToastContainer />
    </>
  );
});

export default CategoryMonitoring;
